import { FC, FormEventHandler, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import ErrorDialog from 'components/organization/dialog/error-dialog';
import SuccessDialog from 'components/organization/dialog/success-dialog';
import ControlledDataTable from 'components/tables/controlled-data-table';
import { DataTableRef } from 'components/tables/data-table';
import {
  ControlledDataTableColumn,
  ControlledDataTableProvider,
  useDataTableContext
} from 'components/tables/data-table-context';
import { useUnitApi } from 'hooks/use-unit-api';
import { downloadUngroupedCsv, fetchActiveUnitsUngrouped } from 'services';
import { ActiveUnit, EquipmentType, UnitGrade, UnitStatus } from 'types';
import { Filter, FilterOperator, FilterState, LogicalOperator } from 'utils';

import unitDataColumns from '../grid/unit-columns';

import { Loader } from 'components';
import { useLookups, useSizeTypes } from 'contexts';
import 'components/inputs/text-field/text-field.scss';
import useConfirm from 'hooks/use-confirm';
import './bulk.scss';
import ImportUnitNumbers from './import-unit-numbers';

const getUpdateValueLabel = (field: string) => {
  switch (field) {
    case 'onHold':
      return 'HOLD STATUS *';
    case 'unitGrade':
      return 'UNIT GRADE *';
    case 'unitStatus':
      return 'UNIT STATUS *';
    case 'sizeType':
      return 'SIZE TYPE *';
    default:
      return 'VALUE *';
  }
};

const getUpdateNotesField = (field: string) => {
  switch (field) {
    case 'onHold':
      return 'holdNotes';
    case 'unitStatus':
      return 'statusReason';
    case 'unitGrade':
      return 'notes';
    case 'sizeType':
      return '';
    default:
      return 'notes';
  }
};

const getUpdateNotesLabel = (field: string) => {
  switch (field) {
    case 'onHold':
      return 'HOLD NOTES';
    case 'unitStatus':
      return 'STATUS NOTES';
    case 'unitGrade':
      return 'NOTES';
    case 'sizeType':
      return '';
    default:
      return 'NOTES';
  }
};

const bulkUpdateColumns = [
  'unitNumber',
  'customer',
  'sizeType',
  'unitStatus',
  'statusReason',
  'onHold',
  'holdNotes',
  'unitGrade',
  'notes'
];

interface TableCellProps<T extends keyof ActiveUnit> {
  column: ControlledDataTableColumn<ActiveUnit, T>;
  row: ActiveUnit;
  updateField: string;
  updateNotes: string;
  updateValue: ActiveUnit[T];
  sizeTypeLabel: (code?: string | null) => string;
  failed: boolean;
}

const TableCell = <T extends keyof ActiveUnit>({
  column,
  row,
  updateField,
  updateNotes,
  updateValue,
  sizeTypeLabel,
  failed
}: TableCellProps<T>) => {
  const { rowSelection } = useDataTableContext<ActiveUnit>();

  const valueLabel =
    column.field === 'sizeType'
      ? sizeTypeLabel(row.sizeType)
      : column.getValueLabel
      ? column.getValueLabel(row[column.field], row)
      : row[column.field];
  const updateValueLabel =
    column.field === 'sizeType'
      ? sizeTypeLabel(updateValue as string)
      : column.getValueLabel
      ? column.getValueLabel(updateValue, row)
      : updateValue;

  if (column.field === updateField && rowSelection.get(row.id)) {
    return (
      <div className={`d-flex g-2 align-items-center ${failed ? 'failed' : 'updating'}`}>
        <span>{valueLabel}</span>
        <span className="material-icons">arrow_forward</span>
        <span>{updateValueLabel}</span>
      </div>
    );
  }

  if (column.field === getUpdateNotesField(updateField) && rowSelection.get(row.id) && updateNotes.trim()) {
    return (
      <div className={`d-flex g-2 align-items-center ${failed ? 'failed' : 'updating'}`}>
        <span>{valueLabel}</span>
        <span className="material-icons">arrow_forward</span>
        <span>{updateNotes}</span>
      </div>
    );
  }

  return valueLabel;
};

const BulkUpdate: FC = () => {
  const navigate = useNavigate();

  const { getConfirmation, ConfirmationDialog } = useConfirm();
  const { updateOnHold, updateUnitGrade, updateUnitSizeType, updateUnitStatus } = useUnitApi();
  const { unitStatusOptions, unitGradeOptions } = useLookups();
  const { sizeTypeOptions } = useSizeTypes();

  const dataTableRef = useRef<DataTableRef>(null);
  const importRef = useRef<{ openDialog: () => void }>(null);

  const [data, setData] = useState<ActiveUnit[]>([]);
  const [rowSelection, setRowSelection] = useState<Map<number, boolean>>(new Map());
  const [unitNumbers, setUnitNumbers] = useState<string[]>([]);

  const [failedIds, setFailedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState<boolean>(false);
  const [resendEDI, setResendEDI] = useState<boolean>(true);
  const [updateField, setUpdateField] = useState<string>('');
  const [updateValue, setUpdateValue] = useState<string>('');
  const [updateNotes, setUpdateNotes] = useState<string>('');

  const selectedIds: number[] = useMemo(() => {
    return [...rowSelection.entries()].filter(e => e[1]).map(e => e[0]);
  }, [rowSelection]);

  const sizeTypeLabelByCode = useMemo(() => {
    const map = new Map<string, string>();
    sizeTypeOptions.forEach(option => map.set(option.code, option.label));
    return map;
  }, [sizeTypeOptions]);

  const sizeTypeLabel = useCallback(
    (code?: string | null) => (code ? sizeTypeLabelByCode.get(code) ?? code : ''),
    [sizeTypeLabelByCode]
  );

  const selectedUnits = useMemo(() => data.filter(({ id }) => rowSelection.get(id)), [data, rowSelection]);

  const { types: selectedEquipmentTypes, hasUnknown } = useMemo(() => {
    const types = new Set<EquipmentType>();
    let hasUnknown = false;
    selectedUnits.forEach(unit => {
      if (unit.equipmentType) types.add(unit.equipmentType);
      else hasUnknown = true;
    });
    return { types: Array.from(types), hasUnknown };
  }, [selectedUnits]);

  const commonEquipmentType = useMemo(
    () => (!hasUnknown && selectedEquipmentTypes.length === 1 ? selectedEquipmentTypes[0] : undefined),
    [hasUnknown, selectedEquipmentTypes]
  );

  const compatibleSizeTypeOptions = useMemo(
    () => (commonEquipmentType ? sizeTypeOptions.filter(option => option.equipmentType === commonEquipmentType) : []),
    [commonEquipmentType, sizeTypeOptions]
  );

  const sizeTypeIncompatible = updateField === 'sizeType' && selectedUnits.length > 0 && !commonEquipmentType;

  /* The target options are derived from the selected units, so with none selected there is nothing
     to offer yet. Without this the dropdown renders enabled but empty and gives no reason why. */
  const sizeTypeNeedsUnits = updateField === 'sizeType' && selectedUnits.length === 0;

  const submitDisabled = loading || !selectedIds.length || !updateValue || sizeTypeIncompatible;

  const handleConfirm = useCallback(async () => {
    /* run every row update and wait for all of them before tallying failures / refetching —
       a forEach(async …) would resolve handleConfirm before any update settled (stale refetch,
       empty failure set). */
    const results = await Promise.all(
      selectedIds.map(async id => {
        switch (updateField) {
          case 'onHold':
            return { id, ok: !!(await updateOnHold(id, updateNotes, updateValue === 'true')) };
          case 'unitGrade':
            return { id, ok: !!(await updateUnitGrade(id, updateNotes, updateValue as UnitGrade)) };
          case 'unitStatus':
            return { id, ok: !!(await updateUnitStatus(id, resendEDI, updateNotes, updateValue as UnitStatus)) };
          case 'sizeType':
            return { id, ok: !!(await updateUnitSizeType(id, updateValue)) };
          default:
            return { id, ok: true };
        }
      })
    );
    setFailedIds(new Set(results.filter(r => !r.ok).map(r => r.id)));
    dataTableRef.current?.forceFetch();
  }, [
    resendEDI,
    selectedIds,
    updateField,
    updateNotes,
    updateValue,
    updateOnHold,
    updateUnitGrade,
    updateUnitSizeType,
    updateUnitStatus
  ]);

  const handleSubmit: FormEventHandler = useCallback(
    async e => {
      e.preventDefault();
      if (loading || submitDisabled) return;

      const selected = data.filter(({ id }) => rowSelection.get(id));
      const targetLabel = updateField === 'sizeType' ? sizeTypeLabel(updateValue) : updateValue;
      const currentLabel = (unit: ActiveUnit) =>
        updateField === 'onHold'
          ? unit.onHold
            ? 'hold'
            : 'none'
          : updateField === 'unitGrade'
          ? unit.unitGrade ?? 'n/a'
          : updateField === 'unitStatus'
          ? unit.unitStatus ?? 'n/a'
          : updateField === 'sizeType'
          ? sizeTypeLabel(unit.sizeType) || 'n/a'
          : '';

      const response = await getConfirmation(
        `Set ${getUpdateValueLabel(updateField).replace(' *', '')} to ${targetLabel} for ${selected.length} unit${
          selected.length === 1 ? '' : 's'
        }?`,
        selected.map(unit => `${unit.unitNumber} (${currentLabel(unit)})`).join(`\n`)
      );
      if (response) {
        setLoading(true);
        await handleConfirm();
      }
      setLoading(false);
    },
    [
      data,
      rowSelection,
      updateField,
      updateValue,
      sizeTypeLabel,
      loading,
      submitDisabled,
      getConfirmation,
      handleConfirm
    ]
  );

  const fetchData = useMemo(() => fetchActiveUnitsUngrouped({ active: true }), []);
  const fetchCsv = useMemo(() => downloadUngroupedCsv({ active: true }), []);

  const filterState: FilterState = useMemo(
    () => ({
      logicalOperator: LogicalOperator.OR,
      filters: selectedIds
        .map(value => ({ field: 'id', filterOperator: FilterOperator.EQUALS, value } as Filter))
        .concat(unitNumbers.map(value => ({ field: 'unitNumber', filterOperator: FilterOperator.EQUALS, value })))
    }),
    [selectedIds, unitNumbers]
  );

  const renderTableCell = useCallback(
    (column: ControlledDataTableColumn<ActiveUnit>, row: ActiveUnit) => (
      <TableCell
        column={column}
        row={row}
        updateField={updateField}
        updateNotes={updateNotes}
        updateValue={updateField === 'onHold' ? updateValue === 'true' : updateValue}
        sizeTypeLabel={sizeTypeLabel}
        failed={failedIds.has(row.id)}
      />
    ),
    [failedIds, updateField, updateNotes, updateValue, sizeTypeLabel]
  );

  const unitColumns = useMemo(() => {
    return unitDataColumns
      .filter(({ field }) => bulkUpdateColumns.includes(field))
      .sort((c0, c1) => bulkUpdateColumns.indexOf(c0.field) - bulkUpdateColumns.indexOf(c1.field));
  }, []);

  return (
    <Loader loading={loading}>
      <div className="page" id="bulk">
        <ConfirmationDialog />
        <ImportUnitNumbers ref={importRef} handleConfirm={setUnitNumbers} />
        <ErrorDialog />
        <SuccessDialog />
        <div className="page-header g-5">
          <h1>BULK UPDATE</h1>
          <div className="grid-2">
            <button
              className="align-self-center btn-back d-flex g-2"
              onClick={() => navigate('/units', { replace: true })}
            >
              <span>BACK TO YARD MANAGEMENT</span>
              <span className="material-icons icon-large">grid_view</span>
            </button>
            <button className="btn-outline" onClick={() => navigate('/units/bulk/transfer', { replace: true })}>
              ACCOUNT TRANSFER
            </button>
          </div>
        </div>
        <div className="page-content overflow-hidden">
          <ControlledDataTableProvider<ActiveUnit>
            initialColumns={bulkUpdateColumns}
            data={data}
            setData={setData}
            filterState={filterState}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
          >
            <ControlledDataTable<ActiveUnit>
              checkboxSelection
              columns={unitColumns}
              dataTableRef={dataTableRef}
              fetchCsv={fetchCsv}
              fetchData={fetchData}
              filterByLocation
              renderCell={renderTableCell}
              searchLogicalOperator={LogicalOperator.OR}
              tableToolbar
            >
              <div className="h-100 mx-2">
                <button
                  className="btn-outline grid-1"
                  onClick={e => {
                    e.stopPropagation();
                    importRef.current?.openDialog();
                  }}
                  type="button"
                >
                  <span className="material-icons icon-large">post_add</span>
                </button>
              </div>
              <div className="flex-grow-1 d-flex justify-content-end">
                <form className="flex-grow-1 grid-5" onSubmit={handleSubmit}>
                  {updateField !== 'unitStatus' ? (
                    <div />
                  ) : (
                    <div className="text-field justify-self-center">
                      <input
                        type="checkbox"
                        checked={resendEDI}
                        onChange={e => setResendEDI(e.target.checked)}
                        style={{ margin: '8px 16px 0', outline: 'none', width: 16 }}
                      />
                      <label className="white-space-nowrap">RESEND EDI</label>
                    </div>
                  )}
                  <div className="text-field h-100">
                    <select
                      required
                      className="h-100 w-100"
                      id="update-field-select"
                      name="updateField"
                      value={updateField}
                      onChange={e => {
                        setUpdateField(e.target.value);
                        setUpdateValue('');
                        setUpdateNotes('');
                      }}
                    >
                      <option value="" disabled />
                      <option value="unitGrade">UNIT GRADE</option>
                      <option value="unitStatus">UNIT STATUS</option>
                      <option value="onHold">HOLD STATUS</option>
                      <option value="sizeType">SIZE TYPE</option>
                    </select>
                    <label htmlFor="update-field-select">UPDATE FIELD *</label>
                  </div>
                  <div className="text-field h-100">
                    <select
                      required
                      className="h-100 w-100"
                      disabled={
                        !updateField || (updateField === 'sizeType' && (sizeTypeNeedsUnits || sizeTypeIncompatible))
                      }
                      id="update-value-select"
                      name="updateValue"
                      value={updateValue}
                      onChange={e => setUpdateValue(e.target.value)}
                    >
                      <option value="" disabled />
                      {updateField === 'unitGrade' &&
                        unitGradeOptions.map(unitGrade => (
                          <option key={unitGrade} value={unitGrade}>
                            {unitGrade}
                          </option>
                        ))}
                      {updateField === 'unitStatus' &&
                        unitStatusOptions.map(unitStatus => (
                          <option key={unitStatus} value={unitStatus}>
                            {unitStatus}
                          </option>
                        ))}
                      {updateField === 'onHold' && (
                        <>
                          <option value="true">HOLD</option>
                          <option value="false">NONE</option>
                        </>
                      )}
                      {updateField === 'sizeType' &&
                        compatibleSizeTypeOptions.map(option => (
                          <option key={option.code} value={option.code}>
                            {option.label}
                          </option>
                        ))}
                    </select>
                    <label htmlFor="update-field-select">{getUpdateValueLabel(updateField)}</label>
                  </div>
                  {updateField === 'sizeType' ? (
                    sizeTypeIncompatible ? (
                      <div className="text-field h-100 align-self-center justify-self-center">
                        <span className="text-danger white-space-nowrap">SELECT COMPATIBLE UNITS</span>
                      </div>
                    ) : sizeTypeNeedsUnits ? (
                      <div className="text-field h-100 align-self-center justify-self-center">
                        <span className="white-space-nowrap">SELECT UNITS FIRST</span>
                      </div>
                    ) : (
                      <div />
                    )
                  ) : (
                    <div className="text-field h-100">
                      <input
                        className={`h-100 w-100 ${!updateNotes.trim() ? 'blank' : ''}`}
                        id="update-notes-input"
                        name="updateNotes"
                        placeholder="Leave blank to keep existing notes"
                        disabled={!updateField}
                        value={updateNotes}
                        onChange={e => setUpdateNotes(e.target.value)}
                      />
                      <label htmlFor="update-notes-input">{getUpdateNotesLabel(updateField)}</label>
                    </div>
                  )}
                  <button className="btn-primary h-100" disabled={submitDisabled} key={rowSelection.size} type="submit">
                    UPDATE ({rowSelection.size})
                  </button>
                </form>
              </div>
            </ControlledDataTable>
          </ControlledDataTableProvider>
        </div>
      </div>
    </Loader>
  );
};

export default BulkUpdate;
